import React, { useState } from "react";
import { useTutorial } from "../context/TutorialContext";
import { getScreenTutorial, GENERAL_TUTORIAL } from "../data/tutorialSteps";
import { colors, shadows, radius } from "../utils/designTokens";
import { FaQuestion, FaBookOpen, FaRedo } from "react-icons/fa";

/**
 * Boton flotante de ayuda que aparece en cada pantalla admin.
 * Muestra el tutorial especifico de la pantalla actual.
 *
 * Props:
 *  - screenId: ID de la pantalla actual (ej: "homeadmin", "pagosedificio")
 */
const TutorialButton = ({ screenId, variant = "floating" }) => {
  const { startTutorial, activeTutorial } = useTutorial();
  const [menuOpen, setMenuOpen] = useState(false);

  // No mostrar si hay un tutorial activo
  if (activeTutorial) return null;

  const screenTutorial = getScreenTutorial(screenId);

  const handleScreenTutorial = () => {
    if (screenTutorial) {
      startTutorial(screenTutorial, true);
    }
    setMenuOpen(false);
  };

  const handleGeneralTutorial = () => {
    startTutorial(GENERAL_TUTORIAL, true);
    setMenuOpen(false);
  };

  const isHeader = variant === "header";

  return (
    <div
      style={isHeader
        ? { position: "relative", zIndex: 9998 }
        : { position: "fixed", bottom: 24, right: 24, zIndex: 9998 }
      }
    >
      {/* Menu desplegable */}
      {menuOpen && (
        <>
          {/* Backdrop invisible para cerrar */}
          <div
            style={{ position: "fixed", inset: 0, zIndex: 9997 }}
            onClick={() => setMenuOpen(false)}
          />
          <div
            style={{
              position: "absolute",
              ...(isHeader
                ? { top: "calc(100% + 8px)", right: 0 }
                : { bottom: 56, right: 0 }),
              width: 220,
              background: colors.white,
              borderRadius: radius.md,
              boxShadow: shadows.lg,
              overflow: "hidden",
              zIndex: 9999,
            }}
          >
            {screenTutorial && (
              <button
                onClick={handleScreenTutorial}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "12px 16px",
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 400,
                  color: colors.gray700,
                  textAlign: "left",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = colors.gray50)
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                <FaBookOpen size={14} color={colors.primary} />
                <div>
                  <div style={{ fontSize: 13, color: colors.black }}>
                    Ayuda de esta pantalla
                  </div>
                  <div style={{ fontSize: 11, color: colors.gray400, marginTop: 2 }}>
                    {screenTutorial.title}
                  </div>
                </div>
              </button>
            )}
            <button
              onClick={handleGeneralTutorial}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "12px 16px",
                border: "none",
                borderTop: screenTutorial
                  ? `1px solid ${colors.gray100}`
                  : "none",
                background: "none",
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 400,
                color: colors.gray700,
                textAlign: "left",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = colors.gray50)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "transparent")
              }
            >
              <FaRedo size={14} color={colors.primary} />
              <div>
                <div style={{ fontSize: 13, color: colors.black }}>
                  Tutorial general
                </div>
                <div style={{ fontSize: 11, color: colors.gray400, marginTop: 2 }}>
                  Recorrido completo de Habitia
                </div>
              </div>
            </button>
          </div>
        </>
      )}

      {/* Boton principal */}
      <button
        onClick={() => setMenuOpen(!menuOpen)}
        style={{
          width: isHeader ? 32 : 48,
          height: isHeader ? 32 : 48,
          borderRadius: radius.full,
          border: "none",
          background: colors.primary,
          color: colors.white,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          boxShadow: isHeader ? "none" : shadows.lg,
          transition: "all 0.2s ease",
          position: "relative",
          zIndex: 9999,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = colors.primaryDark;
          e.currentTarget.style.transform = "scale(1.08)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = colors.primary;
          e.currentTarget.style.transform = "scale(1)";
        }}
        title="Ayuda y tutoriales"
        aria-label="Abrir menu de ayuda"
      >
        <FaQuestion size={isHeader ? 13 : 18} />
      </button>
    </div>
  );
};

export default TutorialButton;
